// What the Console does: send a request, reject an answer, run a lesson. Pages
// call these; they talk to the Consumer API and update the store. See Design >
// Exchange, "Consumer API", and Design > Console, "Lessons".

import { createApi, describeFailure, type Api } from "../api.ts";
import { isRefusedSession, type Auth } from "../../../ui/src/index.ts";
import { askOf, answersOf, chargesOf, refsOf, tagsOf } from "../domain/answer.ts";
import { shapeBatches, shapesToFetchOf } from "../domain/shapes.ts";
import { buildContent } from "../domain/examples.ts";
import { checklist, chipFor, coachText, currentStep, nextState, sendGated, startLesson, type LessonEvent } from "../domain/lesson.ts";
import { EMPTY_REQUEST_TEXT, formatRequest, parseRequestText, validateRequest, withFirstQuestion } from "../domain/request.ts";
import type { AskRequest } from "../domain/types.ts";
import { navigate } from "./router.ts";
import { getState, setState, type ConsoleState } from "./store.ts";

let api: Api;
let auth: Auth | null = null;

/** Sets the AuthKit session, or null where real sign-in isn't configured. Call before initApi. */
export const initAuth = (a: Auth | null) => { auth = a; };
export const initApi = (a?: Api) => {
  api = a ?? createApi(getState().config.apiBase, undefined, () => (auth ? auth.token() : null), (e) => {
    // A token that failed to refresh for a moment, such as with several tabs
    // open, sends no bearer at all; AuthKit ends the session itself if the
    // refresh keeps failing, so only a refused token ends it here.
    if (e.reason === "MissingBearer" && auth?.user()) return;
    sessionLost(describeFailure(e), isRefusedSession(e));
  });
};

export const lessonOf = (s: ConsoleState) => (s.lesson ? s.content.lessons.find((l) => l.id === s.lesson?.lessonId) ?? null : null);

/** The request, as far as it parses, and what is wrong with it. */
export function readRequest(s: ConsoleState) {
  const { request, error } = parseRequestText(s.requestText);
  const problems = error ? [error] : request && Object.keys(request).length > 0 ? validateRequest(request).map((p) => p.message) : [];
  return { request, problems, empty: !!request && Object.keys(request).length === 0 };
}

function advanceLesson(event: LessonEvent) {
  const s = getState();
  const lesson = lessonOf(s);
  if (!lesson || !s.lesson) return;
  setState({ lesson: nextState(lesson, s.lesson, event) });
}

const lastAsk = (s: ConsoleState) => {
  const first = s.response?.kind === "ok" ? s.response.value.questions[0] : undefined;
  return first ? askOf(first) : null;
};

/** Changes the request text. A lesson watches for the edit it is waiting for. */
export function setRequestText(text: string) {
  setState({ requestText: text, stale: getState().response !== null });
  const s = getState();
  const { request } = parseRequestText(text);
  if (request) advanceLesson({ type: "edited", request, ask: lastAsk(s) });
}

export const setRequest = (req: AskRequest) => setRequestText(formatRequest(req));

export function pick(what: string, text = "") {
  setRequestText(formatRequest(withFirstQuestion(null, { id: "q1", text })));
  advanceLesson({ type: "picked", what });
}

/** Applies the lesson's ghost chip. */
export function applyChip() {
  const s = getState();
  const lesson = lessonOf(s);
  if (!lesson || !s.lesson) return;
  const chip = chipFor(lesson, s.lesson, lastAsk(s));
  if (chip) setRequest(withFirstQuestion(parseRequestText(s.requestText).request, chip.set));
}

export function startLessonById(id: string | null) {
  const s = getState();
  const lesson = id ? s.content.lessons.find((l) => l.id === id) : null;
  setState({
    lesson: lesson ? startLesson(lesson) : null,
    requestText: lesson?.start ? formatRequest(lesson.start) : EMPTY_REQUEST_TEXT,
    response: null, stale: false, view: "answer", rejecting: null,
  });
}

/** Reverts the playground to the start of the lesson, or to empty. */
export const revert = () => startLessonById(getState().lesson?.lessonId ?? null);
export const skipLesson = () => setState({ lesson: null });
export const clearPlayground = () => setState({ lesson: null, requestText: EMPTY_REQUEST_TEXT, response: null, stale: false, rejecting: null });

export function sendBlocked(s: ConsoleState): string | null {
  if (s.sending) return "Sending";
  const { problems, empty } = readRequest(s);
  if (empty) return "Start a request to send it";
  const [problem] = problems;
  if (problem !== undefined) return problem;
  const lesson = lessonOf(s);
  if (lesson && s.lesson && sendGated(lesson, s.lesson)) return "Finish this step to send";
  return null;
}

export async function send() {
  const s = getState();
  if (sendBlocked(s)) return;
  const { request } = readRequest(s);
  if (!request) return;
  setState({ sending: true, notice: null });
  const result = await api.ask(request);
  if (!result.ok) { setState({ sending: false, response: { kind: "error", error: result.error }, stale: false }); return; }
  const res = result.value;
  // Every question's answers are charged, so each one goes into this session's ledger.
  const ledger = [...chargesOf(res, Date.now()), ...getState().ledger];
  setState({ sending: false, response: { kind: "ok", value: res, at: Date.now() }, stale: false, balance: res.balance ?? getState().balance, ledger, view: "answer" });
  // Lessons send one question at a time.
  const q = res.questions[0];
  if (q) advanceLesson({ type: "sent", tags: tagsOf(q) });
  void loadTitles(res.questions.flatMap((x) => x.responses.flatMap((r) => (r._tag === "Answer" ? refsOf(r.answer) : []))));
  void loadShapes(res.questions.flatMap((x) => answersOf(x).flatMap(shapesToFetchOf)));
}

// The most refs one GET /v1/shapes request takes. See Design > Exchange,
// "Fetch shape definitions".
const MAX_SHAPE_REFS = 20;

/**
 * Fetches the definitions of an answer's assertion shapes, once each, for
 * their field descriptions. A ref is marked as asked for before its request,
 * so a second answer doesn't ask again, and stays marked when it comes back
 * missing or refused. A failed request unmarks its refs, so a later answer
 * retries them.
 */
export async function loadShapes(refs: string[]) {
  for (const batch of shapeBatches(refs, getState().shapes, MAX_SHAPE_REFS)) {
    setState((cur) => ({ shapes: { ...cur.shapes, ...Object.fromEntries(batch.map((r) => [r, null])) } }));
    const result = await api.shapes(batch);
    setState((cur) => {
      const shapes = { ...cur.shapes };
      for (const r of batch) {
        if (result.ok) shapes[r] = result.value.shapes[r] ?? null;
        else delete shapes[r];
      }
      return { shapes };
    });
  }
}

/** Fetches titles for the Things in an answer, once each, 50 refs a call, the most GET /v1/things takes. */
export async function loadTitles(refs: string[]) {
  const have = getState().titles;
  const all = [...new Set(refs)].filter((r) => !(r in have));
  for (let i = 50; i < all.length; i += 50) void loadTitles(all.slice(i, i + 50));
  const want = all.slice(0, 50);
  if (want.length === 0) return;
  const result = await api.things(want);
  if (!result.ok) return;
  const titles: Record<string, string> = {};
  for (const t of result.value.things) titles[t.wref] = t.title;
  // A Thing's data names the Things it points to by durable ID; the response says what each is called.
  for (const [id, v] of Object.entries(result.value.refs)) if (v.title) { titles[id] = v.title; titles[v.wref] ??= v.title; }
  // The answer names Things by pinned wref; match the title back by the unpinned name.
  for (const ref of want) titles[ref] ??= Object.entries(titles).find(([w]) => ref.startsWith(w.replace(/@v\d+$/, "")))?.[1] ?? "";
  setState((cur) => ({ titles: { ...cur.titles, ...Object.fromEntries(Object.entries(titles).filter(([, v]) => v)) } }));
}

export const openReject = (answerId: string, hub: string, price: number) => setState({ rejecting: { answerId, hub, price } });
export const closeReject = () => setState({ rejecting: null });

export async function confirmReject(reason: string) {
  const r = getState().rejecting;
  if (!r) return;
  const result = await api.reject(r.answerId, reason.trim() || undefined);
  if (!result.ok) { setState({ rejecting: null, notice: describeFailure(result.error) }); return; }
  const { amount, balance } = result.value;
  setState((s) => ({
    rejecting: null, refunded: [...s.refunded, r.answerId], balance: balance ?? s.balance,
    ledger: [{ type: "Refund", what: "Refund for an answer", hub: r.hub, amount, at: Date.now(), answerId: r.answerId }, ...s.ledger],
  }));
  advanceLesson({ type: "rejected" });
  if (getState().creditsOpen) void loadAccount();
}

/** Reads the consumer account: the balance and the ledger. A failure leaves what's shown as it was. */
export async function loadAccount() {
  const result = await api.account();
  if (!result.ok) return;
  setState((s) => ({ account: result.value, balance: result.value.balance ?? s.balance }));
}

/** Builds the examples and lessons from the hubs the environment lists. A failure leaves them empty and says so. */
export async function loadExamples() {
  setState({ examples: "loading" });
  const result = await api.examples();
  if (!result.ok) { setState({ examples: "failed", notice: describeFailure(result.error) }); return; }
  setState((s) => ({ examples: "ready", content: { ...s.content, ...buildContent(result.value) } }));
}

export function openCredits() {
  setState({ creditsOpen: true });
  advanceLesson({ type: "credits-opened" });
  void loadAccount();
}
export const closeCredits = () => setState({ creditsOpen: false });

/** Signs in with WarmHub, which leaves the page for AuthKit. Without AuthKit, sign-in isn't configured and nothing happens. */
export function signIn() { void auth?.signIn(); }
export function signOut() {
  setState({ creditsOpen: false, user: null, balance: null, ledger: [], account: null, examples: "idle" });
  auth?.signOut();
}

/**
 * The session ended: AuthKit couldn't refresh it, or the Exchange refused the
 * caller, with `reason` saying why. Back to signed out, in this page; with a
 * reason, the sign-in page shows it. `refused` is for a refused account, which
 * the sign-in page answers with sign-out instead of signing in again.
 */
export function sessionLost(reason?: string, refused = false) {
  // Without a new reason, an earlier one stays on the sign-in page.
  setState((s) => ({ signedIn: false, user: null, balance: null, creditsOpen: false, notice: null, account: null,
    signedOutReason: reason ?? s.signedOutReason, signedOutRefused: reason ? refused : s.signedOutRefused }));
  if (reason) navigate("sign-in");
}

const welcomedKey = (userId: string) => `console.welcomed.${userId}`;
const welcomedBefore = (userId: string) => { try { return localStorage.getItem(welcomedKey(userId)) === "1"; } catch { return true; } };
const markWelcomed = (userId: string) => { try { localStorage.setItem(welcomedKey(userId), "1"); } catch { /* storage can be blocked */ } };

/**
 * Called once after a page loads signed in: tells the Consumer API who signed
 * in, which creates the WarmHub user and the consumer account where sign-up is
 * open, and shows the balance. A first sign-in on this browser lands on Welcome.
 */
export async function startSession() {
  const who = auth?.user();
  if (!who) return;
  const result = await api.session();
  if (!result.ok) {
    // A refusal has already ended the session and moved to sign-in.
    if (getState().signedIn) setState({ notice: describeFailure(result.error) });
    return;
  }
  const { account, balance, email, userId } = result.value;
  setState({ user: { email: email ?? who.email, account }, balance, signedIn: true, signedOutReason: null, signedOutRefused: false });
  void loadAccount();
  void loadExamples();
  if (!welcomedBefore(userId)) {
    markWelcomed(userId);
    navigate("welcome");
  }
}

export const lessonView = (s: ConsoleState) => {
  const lesson = lessonOf(s);
  if (!lesson || !s.lesson) return null;
  return { lesson, state: s.lesson, step: currentStep(lesson, s.lesson), coach: coachText(lesson, s.lesson), items: checklist(lesson, s.lesson), chip: chipFor(lesson, s.lesson, lastAsk(s)) };
};

/** Opens the Playground with a question and bid from the content files, such as a use case on Home. */
export function startFromText(text: string, bid: number) {
  setState({ lesson: null, requestText: formatRequest({ questions: [{ id: "q1", text, maxCostPerAnswer: bid }] }), response: null, stale: false, view: "answer", rejecting: null });
  navigate("playground");
}
